import type { BaseDocument } from "./data";
import type { Floor } from "./floor";
import type { GeoJSONPoint } from "./map";
import type { Sensor } from "./sensor";
import type { Zone } from "./zone";

/**
 * Display mode for the location used in the map
 */
export enum DisplayMode {
	HYBRID = "hybrid",
	ROADMAP = "roadmap",
	SATELLITE = "satellite",
}

/**
 * Status of the location used to determine if the location is responsive
 */
export enum LocationStatus {
	ACTIVE = "Active",
	ALL_SENSORS_RESPONDING = "All Sensors Responding",
	INACTIVE = "Inactive",
	SENSORS_UNRESPONSIVE = "Sensors Unresponsive",
}

/**
 * Parameters for creating a new Location
 */
export type CreateLocationParams = Omit<
	Location,
	"createdAt" | "displayMode" | "id" | "status" | "updatedAt"
> & {
	displayMode?: DisplayMode;
	status?: LocationStatus;
};

/**
 * Location represents a physical place (like "Main Office Building")
 */
export interface Location extends BaseDocument {
	/** Optional physical address */
	address?: LocationAddress;
	/** Optional geographic coordinates as GeoJSON Point */
	coordinates?: GeoJSONPoint;
	/** Optional display mode for the location */
	displayMode: DisplayMode;
	/**
	 * Floors for this location. Populated when locations are loaded; not stored
	 * on the location document. Zones without a floor stay on `zones`.
	 */
	floors?: Floor[];
	/** Name of the location (required) */
	name: string;
	/** Optional sensors associated with the location */
	sensors?: Sensor[];
	/** Optional status of the location */
	status: LocationStatus;
	/** Optional IANA timezone identifier (e.g., "America/New_York") */
	timezone?: string;
	/** Optional zones associated with the location */
	zones?: Zone[];
	/** Optional zoom level for the location */
	zoomLevel?: number;
}

/**
 * Physical address for a location
 */
export interface LocationAddress {
	city?: string;
	country?: string;
	isDefault?: boolean;
	label?: string;
	name?: string;
	phone?: string;
	postalCode?: string;
	stateProvince?: string;
	street1?: string;
	street2?: string;
}
